"use client";

import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import Image from "next/image";
import { groq } from "next-sanity";
import { type GetStaticPaths, type GetStaticProps, type NextPage } from "next";
import { getPageMetaImage } from "utils";
import { ArticleJsonLd } from "next-seo";
import { MDXRemote, type MDXRemoteSerializeResult } from "next-mdx-remote";
import { Box, Text, Flex, Accordion, SimpleGrid } from "@chakra-ui/react";
import {
  type Playbook,
  type Banner,
  getPlaybookPost,
  getClient,
  getAnnouncementBanner,
  type RelatedPlaybook,
} from "lib/sanity";
import { trackEvent } from "lib/analytics";
import { components } from "components/mdx/components";
import { Page } from "components/page";
import { Article, ArticleContainer } from "components/article";
import { type SidebarHeading, ArticleSidebar } from "components/sidebar";
import { RelatedPlaybookCard } from "components/playbooks/related-playbook-card";
import { useHeaderHeight } from "contexts/header-height";
import { Badge } from "ui/badge";
import NotFound from "pages/404";
import { HubSpotForm } from "components/hubspot";
import { getArticleHeadings, serializeArticle } from "components/mdx/utils";
import { Section, sectionSpacingY } from "components/section";
import { trackNewsletterSignup } from "components/event-tracking";
import { blogBlurDataUrl } from "../../constants/blur-data-urls";

type PageProps = {
  post: Playbook;
  slug: string;
  mdx: MDXRemoteSerializeResult;
  headings: SidebarHeading[];
  relatedPlaybooks: RelatedPlaybook[] | undefined;
  announcementBanner?: Banner;
};

const DEFAULT_IMAGE = "https://hightouch.com/assets/meta.png";
const HUBSPOT_FIELDS = ["firstname", "lastname", "company"];
const HUBSPOT_FORM_ID = "bc652bd7-a30c-4927-98b9-cb52ce97bfc2";

const PlaybookPost: NextPage<Readonly<PageProps>> = ({
  post,
  slug,
  mdx,
  headings,
  relatedPlaybooks,
  announcementBanner,
}) => {
  const router = useRouter();
  const { headerHeight } = useHeaderHeight();
  const [success, setSuccess] = useState(false);

  const onFormSubmit = (lead: Record<string, string>) => {
    trackNewsletterSignup("Playbook of the Month", lead);
    setSuccess(true);
  };

  useEffect(() => {
    trackEvent("Playbook Post Viewed", {
      post_name: post?.title,

      post_author: post?.authors?.[0].name ?? "",

      post_slug: post?.slug,
    });
  }, [post.title, post.authors, post.slug]);

  if (!router.isFallback && (!slug || !post)) {
    return <NotFound />;
  }

  if (!post || !mdx) {
    return null;
  }

  const filteredIntegrations = post.integrations.filter(Boolean);
  const filteredExtensions = post.extensions?.filter(Boolean);
  const filteredRelatedPlaybooks = relatedPlaybooks?.filter(Boolean);

  return (
    <Page
      announcementBanner={announcementBanner}
      title={post.metaTitle ?? post.title}
      description={post.metaDescription ?? post.excerpt}
      image={
        post.imageUrl
          ? getPageMetaImage({
              src: `${post.imageUrl}?w=1200`,
              alt: post.metaTitle ?? post.title,
            })
          : undefined
      }
    >
      <Section as="article" my={0} mt={{ base: 12, md: 16 }} pb={sectionSpacingY}>
        <Flex gap={2} wrap="wrap" mb="small">
          <Badge bg="new.yellow.50">{post.industry}</Badge>
          <Badge bg="new.blue.50">{post.experienceLevel}</Badge>
          <Badge bg="new.green.50">{post.team}</Badge>
        </Flex>

        <Text as="h1" textStyle={{ base: "article.h3", md: "article.h2" }} maxW="3xl">
          {post.title}
        </Text>

        <ArticleJsonLd
          type="Blog"
          url={`https://hightouch.com/playbooks/${post.slug}`}
          title={post.metaTitle ?? post.title}
          images={[post.imageUrl ? `${post.imageUrl}?w=200&h=200&fit=crop&crop=entropy` : DEFAULT_IMAGE]}
          datePublished={post._createdAt}
          dateModified={post._updatedAt}
          authorName={post.authors?.[0].name ?? "Hightouch"}
          description={post.metaDescription ?? post.excerpt}
        />

        <Box mt={8} pos="relative">
          <Flex
            gap={16}
            fontFamily="inter"
            justify={{ base: "start", md: "space-between" }}
            wrap={{ base: "wrap", lg: "nowrap" }}
          >
            <Flex direction="column" gap="bigger" maxW="3xl">
              <Text as="h2" color="brand.textSecondary.light" textStyle="article.p" maxW="2xl" mb={0}>
                {post.excerpt}
              </Text>

              <Flex flexGrow={1} gap="smallest" color="brand.textSecondary.light" fontSize="brand.sm" wrap="wrap">
                <Text>Made by: {post.authors.map(({ name }) => name).join(", ")}</Text>
              </Flex>
            </Flex>

            <Flex
              width={{ base: "100%", lg: "300px" }}
              flexShrink={0}
              justify={{ base: "flex-start", lg: "flex-end" }}
              align={{ base: "flex-start", lg: "flex-end" }}
            >
              {(filteredIntegrations.length > 0 || filteredExtensions) && (
                <Flex gap={6} wrap="wrap">
                  {filteredIntegrations.map((integration) => (
                    <Box
                      key={integration.slug}
                      boxSize={{ base: "48px", lg: "72px" }}
                      inset={0}
                      pointerEvents="none"
                      position="relative"
                    >
                      <Image
                        fill
                        src={integration.iconUrl}
                        alt={integration.name + "."}
                        style={{ objectFit: "contain" }}
                      />
                    </Box>
                  ))}

                  {filteredExtensions?.map((extension) => (
                    <Box
                      key={extension.slug}
                      boxSize={{ base: "48px", lg: "72px" }}
                      inset={0}
                      pointerEvents="none"
                      position="relative"
                    >
                      <Image fill src={extension.iconUrl} alt={extension.name + "."} style={{ objectFit: "contain" }} />
                    </Box>
                  ))}
                </Flex>
              )}
            </Flex>
          </Flex>

          <ArticleContainer
            mt={{ base: "big", lg: "biggest" }}
            pt={{ base: "big", lg: "biggest" }}
            borderTop="1px solid"
            borderColor="brand.border.light"
            maxW="100%"
          >
            <ArticleSidebar
              top={`${headerHeight + 20}px`}
              headings={headings}
              path={`playbooks/${slug}`}
              cta={
                <Box background="brand.backgroundSecondary.light" borderRadius="base" p="small">
                  <Text mb="smaller" textStyle="body3" fontWeight="semibold">
                    Subscribe to Playbook of the Month
                  </Text>

                  <HubSpotForm
                    label="Subscribe"
                    displayedFields={HUBSPOT_FIELDS}
                    formId={HUBSPOT_FORM_ID}
                    successContent={success && <Text textStyle="caption1">Thank you for subscribing!</Text>}
                    onSubmit={onFormSubmit}
                  />
                </Box>
              }
            />
            <Article>
              {post.imageUrl && (
                <Box pos="relative" width="100%" maxW="container.article" mb={{ base: 12, md: 16 }}>
                  <Image
                    src={`${post.imageUrl}?w=2000`}
                    alt={`${post.title}.`}
                    placeholder="blur"
                    blurDataURL={blogBlurDataUrl}
                    sizes="100%"
                    width={800}
                    height={400}
                    style={{
                      objectFit: "contain",
                      width: "100%",
                      height: "auto",
                      border: "1px solid",
                      borderColor: "var(--chakra-colors-base-border)",
                      borderRadius: "var(--chakra-radii-base)",
                    }}
                  />
                </Box>
              )}
              {/* Open first and last accordions by default */}
              <Accordion allowToggle allowMultiple defaultIndex={[0, headings.length - 1]}>
                <MDXRemote lazy components={components({ publishedAt: post._createdAt, type: "playbook" })} {...mdx} />
              </Accordion>
              {filteredRelatedPlaybooks && filteredRelatedPlaybooks.length === 2 && (
                <Box mt={{ base: 16, md: 24, lg: 32 }}>
                  <Text as="h2" textStyle={{ base: "article.h4", md: "article.h3" }} mb={10}>
                    Related playbooks
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={7}>
                    {filteredRelatedPlaybooks.map((playbook) => (
                      <RelatedPlaybookCard key={playbook.slug} {...playbook} />
                    ))}
                  </SimpleGrid>
                </Box>
              )}
            </Article>
          </ArticleContainer>
        </Box>
      </Section>
    </Page>
  );
};

export const getStaticProps: GetStaticProps<PageProps> = async ({ params }) => {
  const slug = params?.slug ? String(params.slug) : "";
  const [post, announcementBanner] = await Promise.all([getPlaybookPost(slug), getAnnouncementBanner()]);

  if (!post) {
    return {
      notFound: true,
    };
  }

  const mdx = await serializeArticle(post.content, true);

  return {
    props: {
      slug,
      post,
      mdx,
      headings: getArticleHeadings(post.content),
      relatedPlaybooks: post.related,
      announcementBanner,
    },
    revalidate: 60,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs: Array<{ slug: string }> = await getClient(false).fetch(groq`*[_type == "playbook"] { slug }`);

  return {
    paths: slugs.map(({ slug }: { slug: string }) => ({ params: { slug } })),
    fallback: "blocking",
  };
};

export default PlaybookPost;
